import React, { useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Inicio } from '../Dashboard/Inicio';
import { IndicadoresPerfil } from '../Dashboard/IndicadoresPerfil';
import { Desenvolvimento } from '../Dashboard/Desenvolvimento';
import { Atestados } from '../Dashboard/Atestados';
import Gamificacao from '../Dashboard/Gamificacao';
import { ClimaOrganizacional } from '../Dashboard/ClimaOrganizacional';
import { Desligamentos } from '../Dashboard/Desligamentos';
import { Entrevistas } from '../Dashboard/Entrevistas';
import { FaleComRHContainer } from '../Dashboard/FaleComRHContainer';
import { NotFound } from '../Pages/NotFound';

const LayoutContainer = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const MainContent = styled.main.withConfig({
  shouldForwardProp: (prop) => !['sidebarOpen', 'isMobile'].includes(prop),
})<{ sidebarOpen: boolean; isMobile: boolean }>`
  margin-left: ${({ sidebarOpen, isMobile }) => {
    if (isMobile) return '0';
    return sidebarOpen ? '240px' : '60px';
  }};
  margin-top: 20px;
  padding: 0 ${({ theme }) => theme.spacing.md}
    ${({ theme }) => theme.spacing.md};
  transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: calc(100vh - 60px);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: 0;
    padding: 0 ${({ theme }) => theme.spacing.md}
      ${({ theme }) => theme.spacing.md};
  }
`;

const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 80;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: opacity ${({ theme }) => theme.transitions.normal},
    visibility ${({ theme }) => theme.transitions.normal};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

interface LayoutProps {
  userName?: string;
  userRole?: string;
  userAvatar?: string;
  onLogout?: () => void;
}

// Hook para detectar se é mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  return isMobile;
};

export const Layout: React.FC<LayoutProps> = ({
  userName,
  userRole,
  userAvatar,
  onLogout,
}) => {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const location = useLocation();
  const navigate = useNavigate();

  // Ajustar sidebar baseado no tamanho da tela
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNavItemClick = (itemId: string) => {
    // Mapear IDs para rotas - apenas as que realmente existem
    const routeMap: { [key: string]: string } = {
      dashboard: '/',
      home: '/pessoas/indicadores-perfil-populacao',
      'medical-certificates': '/absenteismo/atestados',
      'platform-gamification': '/plataforma-hspw/gamificacao',
      'climate-general-data': '/clima-organizacional/dados-gerais',
      desligamentos: '/desligamentos/dados-gerais',
      entrevistas: '/desligamentos/entrevistas',
      'contact-hr': '/fale-com-rh',
    };

    const route = routeMap[itemId];
    if (route) {
      navigate(route);
    } else {
      // Para outros itens que ainda não têm página, mostrar 404
      navigate('/pagina-em-desenvolvimento');
    }
  };

  // Função para determinar item ativo baseado na rota atual
  const getActiveItem = () => {
    const path = location.pathname;

    // Apenas as rotas que realmente existem
    if (path === '/') return 'dashboard';
    if (path === '/pessoas/indicadores-perfil-populacao') return 'home';
    if (path === '/absenteismo/atestados') return 'medical-certificates';
    if (path === '/plataforma-hspw/gamificacao') return 'platform-gamification';
    if (path === '/clima-organizacional/dados-gerais')
      return 'climate-general-data';
    if (path === '/desligamentos/dados-gerais') return 'desligamentos';
    if (path === '/desligamentos/entrevistas') return 'entrevistas';
    if (path === '/fale-com-rh') return 'contact-hr';

    // Para qualquer outra rota, não destacar nenhum item
    return '';
  };

  return (
    <LayoutContainer>
      <Header
        userName={userName}
        userRole={userRole}
        userAvatar={userAvatar}
        onLogout={onLogout}
        onMenuToggle={handleSidebarToggle}
      />
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={handleSidebarToggle}
        activeItem={getActiveItem()}
        onItemClick={handleNavItemClick}
      />
      <Overlay show={sidebarOpen && isMobile} onClick={handleSidebarToggle} />
      <MainContent sidebarOpen={sidebarOpen} isMobile={isMobile}>
        <Routes>
          <Route path="/" element={<Inicio />} />
          <Route path="/desenvolvimento" element={<Desenvolvimento />} />
          <Route
            path="/pessoas/indicadores-perfil-populacao"
            element={<IndicadoresPerfil />}
          />
          <Route path="/absenteismo/atestados" element={<Atestados />} />
          <Route
            path="/plataforma-hspw/gamificacao"
            element={<Gamificacao />}
          />
          <Route
            path="/clima-organizacional/dados-gerais"
            element={<ClimaOrganizacional />}
          />
          <Route
            path="/desligamentos/dados-gerais"
            element={<Desligamentos />}
          />
          <Route path="/desligamentos/entrevistas" element={<Entrevistas />} />
          <Route path="/fale-com-rh" element={<FaleComRHContainer />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </MainContent>
    </LayoutContainer>
  );
};
